/*
 * @providesModule LiveBracketsButton
 *
 */

import Tournament from 'SKZTournament';
import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {View, Text, Platform} from 'react-native';
import styles, {
  ICON_COLOR,
  PLAY_BUTTON_ANIMATION,
  PLAY_BUTTON_ANIMATION_DURATION,
  getButtonColor,
} from 'LiveBracketsButtonStyle';
import {PlayGradientButton} from 'GradientButtons';
import SkillzSVG from 'SkillzSVG';
import LottieView from 'lottie-react-native';
import {_m} from 'INTL';
import * as Animatable from 'react-native-animatable';
import ClientPropertyConstants from 'ClientPropertyConstants';

type LiveBracketsButtonProps = {
  tournament: Tournament;
  renderWithPlayButton?: boolean;
};

export function LiveBracketsButton({
  tournament,
  renderWithPlayButton,
}: LiveBracketsButtonProps): ReactElement {
  const lottieRef = useRef<LottieView>(null);
  const [shouldPlay, setShouldPlay] = useState(false);
  const [hasAnimationError, setHasAnimationError] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldPlay(true);
      if (lottieRef.current && !hasAnimationError) {
        try {
          lottieRef.current.play();
        } catch (error) {
          setHasAnimationError(true);
        }
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [hasAnimationError]);

  const onAnimationFinish = () => {
    if (lottieRef.current && !hasAnimationError) {
      try {
        lottieRef.current.play();
      } catch (error) {
        setHasAnimationError(true);
      }
    }
  };

  const buttonText = tournament.textForPlayButton();
  const playGradientButtonColor = getButtonColor(tournament);

  // If registration is closed, show only text without button
  if (tournament.isRegistrationClosed() && !tournament.isPlayerRegistered()) {
    return <Text style={styles.playButtonTextLiveBrackets}>{_m('REGISTRATION CLOSED')}</Text>;
  }

  function getButtonIcon(): string {
    if (tournament.isPlayerWaitingForNextRound()) {
      return '';
    }

    // Live brackets "Registered" state
    if (tournament.isPlayerRegistered()) {
      return 'icon_circle_checkmark.svg';
    }

    // Live brackets "Event Full" state
    if (tournament.isBracketFull()) {
      return 'icon_people.svg';
    }

    return 'icon_circle_checkmark.svg';
  }

  if (ClientPropertyConstants.getTournamentTemplateUIV2Enabled()) {
    // For V2, if registration is closed, show only text without button container
    if (tournament.isRegistrationClosed() && !tournament.isPlayerRegistered()) {
      return <Text style={styles.playButtonTextLiveBracketsV2}>{_m('REGISTRATION CLOSED')}</Text>;
    }

    return (
      <View style={styles.playButtonContainerLiveBracketsV2}>
        {tournament.isPlayerWaitingForNextRound() && !hasAnimationError ? (
          <LottieView
            ref={lottieRef}
            source={require('../../../../source_images/lottieAnimations/Loader.json')}
            autoPlay={shouldPlay}
            loop={true}
            speed={0.8}
            style={[styles.playButtonIconLiveBrackets, {marginLeft: 0}]}
            onAnimationFinish={onAnimationFinish}
            onLayout={() => {
              if (lottieRef.current && !hasAnimationError) {
                try {
                  lottieRef.current.play();
                } catch (error) {
                  setHasAnimationError(true);
                }
              }
            }}
          />
        ) : tournament.isPlayerWaitingForNextRound() && hasAnimationError ? (
          <View style={[styles.playButtonIconLiveBrackets, {marginLeft: 0}]} />
        ) : (
          <SkillzSVG
            style={styles.playButtonIconLiveBrackets}
            source={getButtonIcon()}
            tintColor={ICON_COLOR}
          />
        )}
        <Text style={styles.playButtonTextLiveBracketsV2}>{buttonText}</Text>
      </View>
    );
  }

  return renderWithPlayButton ? (
    <View style={[styles.playButton]}>
      <View
        style={[
          styles.playButtonContainerLiveBrackets,
          styles.playButtonContainerPlayButtonAdjustment,
        ]}
      >
        <Text style={styles.playButtonTextLiveBrackets}>{buttonText}</Text>
        <View>
          <PlayGradientButton colors={playGradientButtonColor} style={styles.playButton} />
        </View>
      </View>
    </View>
  ) : (
    <View style={[styles.playButton]}>
      <View style={styles.playButtonContainerLiveBrackets}>
        <Text style={styles.playButtonTextLiveBrackets}>{buttonText}</Text>
        <View>
          {tournament.isPlayerWaitingForNextRound() && !hasAnimationError ? (
            <LottieView
              ref={lottieRef}
              source={require('../../../../source_images/lottieAnimations/Loader.json')}
              autoPlay={shouldPlay}
              loop={true}
              speed={0.8}
              style={[styles.playButtonIconLiveBrackets, {marginLeft: 0}]}
              onAnimationFinish={onAnimationFinish}
              onLayout={() => {
                if (lottieRef.current && !hasAnimationError) {
                  try {
                    lottieRef.current.play();
                  } catch (error) {
                    setHasAnimationError(true);
                  }
                }
              }}
            />
          ) : tournament.isPlayerWaitingForNextRound() && hasAnimationError ? (
            <View style={[styles.playButtonIconLiveBrackets, {marginLeft: 0}]} />
          ) : (
            <SkillzSVG
              style={styles.playButtonIconLiveBrackets}
              source={getButtonIcon()}
              tintColor={ICON_COLOR}
            />
          )}
        </View>
      </View>
    </View>
  );
}

type LiveBracketsReadyToPlayButtonProps = {
  tournament: Tournament;
};

export function LiveBracketsReadyToPlayButton({
  tournament,
}: LiveBracketsReadyToPlayButtonProps): ReactElement {
  const playGradientButtonColor = getButtonColor(tournament);
  const [animationBackgroundColor] = playGradientButtonColor;
  const animationStyles = {backgroundColor: animationBackgroundColor};

  return (
    <View style={styles.playButtonContainerLiveBracketsInFlight}>
      <Text style={[styles.playButtonTextLiveBrackets, styles.playButtonTextLiveBracketsInflight]}>
        {_m('Ready to Play')}
      </Text>
      <View>
        <Animatable.View
          animation={PLAY_BUTTON_ANIMATION}
          easing='ease'
          iterationCount='infinite'
          duration={PLAY_BUTTON_ANIMATION_DURATION}
          style={[styles.playButtonAnimation, animationStyles]}
        />

        <Animatable.View
          animation={PLAY_BUTTON_ANIMATION}
          easing='ease'
          iterationCount='infinite'
          duration={PLAY_BUTTON_ANIMATION_DURATION}
          delay={60}
          style={[styles.playButtonAnimation, animationStyles]}
        />

        <PlayGradientButton colors={playGradientButtonColor} style={styles.playButton} />
      </View>
    </View>
  );
}
